import React from "react";
import { Chip, Tooltip } from "@mui/material";
import type { ChipProps } from "@mui/material";

interface RoleChipProps {
  role: string;
  size?: "small" | "medium";
  onDelete?: () => void;
}

/**
 * Get color for role
 */
const getRoleColor = (role: string): ChipProps["color"] => {
  switch (role) {
    case "ADMIN":
      return "primary";
    case "ACCOUNTANT":
      return "secondary";
    case "MEMBER":
      return "default";
    case "CONTRACTOR":
      return "info";
    case "GOVERNMENT":
      return "warning";
    case "GUEST":
      return "default";
    default:
      return "default";
  }
};

/**
 * Get Finnish label for role
 */
const getRoleLabel = (role: string): string => {
  switch (role) {
    case "ADMIN":
      return "Ylläpitäjä";
    case "ACCOUNTANT":
      return "Kirjanpitäjä";
    case "MEMBER":
      return "Osakas";
    case "CONTRACTOR":
      return "Urakoitsija";
    case "GOVERNMENT":
      return "Viranomainen";
    case "GUEST":
      return "Vieras";
    default:
      return role;
  }
};

/**
 * Get description for role
 */
const getRoleDescription = (role: string): string => {
  switch (role) {
    case "ADMIN":
      return "Täydet oikeudet yhdistyksen hallintaan";
    case "ACCOUNTANT":
      return "Kirjanpito ja laskutus";
    case "MEMBER":
      return "Peruskäyttäjä";
    case "CONTRACTOR":
      return "Urakoitsija - Rajoitetut käyttöoikeudet";
    case "GOVERNMENT":
      return "Viranomainen - Lukuoikeus";
    case "GUEST":
      return "Vieras - Rajoitetut oikeudet";
    default:
      return "";
  }
};

/**
 * Chip component for displaying user roles
 * Color-coded and with tooltips
 */
export const RoleChip: React.FC<RoleChipProps> = ({
  role,
  size = "small",
  onDelete,
}) => {
  const description = getRoleDescription(role);

  const chip = (
    <Chip
      label={getRoleLabel(role)}
      size={size}
      color={getRoleColor(role)}
      onDelete={onDelete}
    />
  );

  // Only wrap in tooltip if there's a description
  if (description) {
    return <Tooltip title={description}>{chip}</Tooltip>;
  }

  return chip;
};
